import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import PageHeader from '../../components/layout/PageHeader';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as CheckoutIcon,
  Add as CheckinIcon,
  ExpandMore as ExpandMoreIcon,
  Warehouse as WarehouseIcon,
  Shelves as ShelvesIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import {
  LocationDto,
  CheckoutInventoryRequest,
  CheckinInventoryRequest,
  getLocations,
  addLocation,
  editLocation,
  deleteLocation,
  addStorage,
  addShelf,
  checkoutInventory,
  checkinInventory
} from '../../services/locationApi';
import { getProducts, ProductDto } from '../../services/expensesApi';

interface LocationFormData {
  name: string;
  description: string;
}

interface StorageFormData {
  name: string;
  description: string;
  locationPublicId: string;
}

interface ShelfFormData {
  name: string;
  description: string;
  storagePublicId: string;
}

interface InventoryFormData {
  productPublicId: string;
  shelfPublicId: string;
  loteNumber: string;
  quantity: number;
  unitCost: number;
  expirationDate: string;
  notes: string;
}

const HierarchicalLocationList: React.FC = () => {
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [storageDialogOpen, setStorageDialogOpen] = useState(false);
  const [shelfDialogOpen, setShelfDialogOpen] = useState(false);
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [inventoryAction, setInventoryAction] = useState<'checkin' | 'checkout'>('checkin');
  
  // Form data
  const [locationForm, setLocationForm] = useState<LocationFormData>({ name: '', description: '' });
  const [storageForm, setStorageForm] = useState<StorageFormData>({ name: '', description: '', locationPublicId: '' });
  const [shelfForm, setShelfForm] = useState<ShelfFormData>({ name: '', description: '', storagePublicId: '' });
  const [inventoryForm, setInventoryForm] = useState<InventoryFormData>({
    productPublicId: '',
    shelfPublicId: '',
    loteNumber: '',
    quantity: 0,
    unitCost: 0,
    expirationDate: '',
    notes: ''
  });
  
  // Edit states
  const [editingLocation, setEditingLocation] = useState<LocationDto | null>(null);
  
  // Expanded states for accordions
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());
  const [expandedStorages, setExpandedStorages] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [locationsData, productsData] = await Promise.all([
        getLocations(),
        getProducts()
      ]);
      setLocations(locationsData);
      setProducts(productsData);
    } catch (err) {
      setError('Falha ao carregar os dados');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async () => {
    try {
      if (editingLocation) {
        await editLocation(editingLocation.publicId, locationForm);
      } else {
        await addLocation(locationForm.name, locationForm.description);
      }
      await loadData();
      setLocationDialogOpen(false);
      setLocationForm({ name: '', description: '' });
      setEditingLocation(null);
    } catch (err) {
      setError('Falha ao guardar o local');
      console.error('Error saving location:', err);
    }
  };

  const handleAddStorage = async () => {
    try {
      await addStorage(storageForm);
      await loadData();
      setStorageDialogOpen(false);
      setStorageForm({ name: '', description: '', locationPublicId: '' });
    } catch (err) {
      setError('Falha ao adicionar o armazenamento');
      console.error('Error adding storage:', err);
    }
  };

  const handleAddShelf = async () => {
    try {
      await addShelf(shelfForm);
      await loadData();
      setShelfDialogOpen(false);
      setShelfForm({ name: '', description: '', storagePublicId: '' });
    } catch (err) {
      setError('Falha ao adicionar a prateleira');
      console.error('Error adding shelf:', err);
    }
  };

  const handleInventoryOperation = async () => {
    try {
      if (inventoryAction === 'checkin') {
        const request: CheckinInventoryRequest = {
          productPublicId: inventoryForm.productPublicId,
          shelfPublicId: inventoryForm.shelfPublicId,
          loteNumber: inventoryForm.loteNumber,
          quantity: inventoryForm.quantity,
          unitCost: inventoryForm.unitCost || 0,
          expirationDate: inventoryForm.expirationDate || undefined,
          notes: inventoryForm.notes
        };
        await checkinInventory(request);
      } else {
        const request: CheckoutInventoryRequest = {
          productPublicId: inventoryForm.productPublicId,
          shelfPublicId: inventoryForm.shelfPublicId,
          loteNumber: inventoryForm.loteNumber,
          quantity: inventoryForm.quantity,
          notes: inventoryForm.notes
        };
        await checkoutInventory(request);
      }
      await loadData();
      setInventoryDialogOpen(false);
      setInventoryForm({
        productPublicId: '',
        shelfPublicId: '',
        loteNumber: '',
        quantity: 0,
        unitCost: 0,
        expirationDate: '',
        notes: ''
      });
    } catch (err) {
      setError(inventoryAction === 'checkin' ? 'Falha ao dar entrada do inventário' : 'Falha ao dar saída do inventário');
      console.error(`Error ${inventoryAction} inventory:`, err);
    }
  };

  const handleDeleteLocation = async (publicId: string) => {
    if (window.confirm('Tem a certeza de que pretende eliminar este local?')) {
      try {
        await deleteLocation(publicId);
        await loadData();
      } catch (err) {
        setError('Falha ao eliminar o local');
        console.error('Error deleting location:', err);
      }
    }
  };

  const openEditLocation = (location: LocationDto) => {
    setEditingLocation(location);
    setLocationForm({ name: location.name, description: location.description || '' });
    setLocationDialogOpen(true);
  };

  const openAddStorage = (locationPublicId: string) => {
    setStorageForm({ name: '', description: '', locationPublicId });
    setStorageDialogOpen(true);
  };

  const openAddShelf = (storagePublicId: string) => {
    setShelfForm({ name: '', description: '', storagePublicId });
    setShelfDialogOpen(true);
  };

  const openInventoryDialog = (shelfPublicId: string, action: 'checkin' | 'checkout') => {
    setInventoryForm(prev => ({ ...prev, shelfPublicId }));
    setInventoryAction(action);
    setInventoryDialogOpen(true);
  };

  const toggleLocationExpanded = (locationId: string) => {
    const newExpanded = new Set(expandedLocations);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedLocations(newExpanded);
  };

  const toggleStorageExpanded = (storageId: string) => {
    const newExpanded = new Set(expandedStorages);
    if (newExpanded.has(storageId)) {
      newExpanded.delete(storageId);
    } else {
      newExpanded.add(storageId);
    }
    setExpandedStorages(newExpanded);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Locais"
        subtitle="Gira os locais, as áreas de armazenamento e as prateleiras para organizar o inventário."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setLocationDialogOpen(true)}
          >
            Adicionar local
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {locations.map((location) => (
        <Card key={location.publicId} sx={{ mb: 2 }}>
          <Accordion 
            expanded={expandedLocations.has(location.publicId)}
            onChange={() => toggleLocationExpanded(location.publicId)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%" onClick={(e) => e.stopPropagation()}>
                <WarehouseIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box flexGrow={1}>
                  <Typography variant="h6">{location.name}</Typography>
                  {location.description && (
                    <Typography variant="body2" color="textSecondary">
                      {location.description}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openAddStorage(location.publicId);
                    }}
                  >
                    Adicionar armazenamento
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditLocation(location);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLocation(location.publicId);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box pl={2}>
                {location.storages?.length > 0 ? (
                  location.storages.map((storage) => (
                    <Card key={storage.publicId} sx={{ mb: 2, ml: 2 }}>
                      <Accordion
                        expanded={expandedStorages.has(storage.publicId)}
                        onChange={() => toggleStorageExpanded(storage.publicId)}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" width="100%" onClick={(e) => e.stopPropagation()}>
                            <StorageIcon sx={{ mr: 2, color: 'secondary.main' }} />
                            <Box flexGrow={1}>
                              <Typography variant="subtitle1">{storage.name}</Typography>
                              {storage.description && (
                                <Typography variant="body2" color="textSecondary">
                                  {storage.description}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                openAddShelf(storage.publicId);
                              }}
                            >
                              Adicionar prateleira
                            </Button>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box pl={2}>
                            {storage.shelves?.length > 0 ? (
                              <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2}>
                                {storage.shelves.map((shelf) => (
                                  <Card variant="outlined" sx={{ height: '100%' }} key={shelf.publicId}>
                                    <CardContent>
                                      <Box display="flex" alignItems="center" mb={2}>
                                        <ShelvesIcon sx={{ mr: 1, color: 'info.main' }} />
                                        <Typography variant="subtitle2">
                                          {shelf.name}
                                        </Typography>
                                      </Box>
                                      {shelf.description && (
                                        <Typography variant="body2" color="textSecondary" mb={2}>
                                          {shelf.description}
                                        </Typography>
                                      )}
                                      
                                      {/* Inventory display */}
                                      {shelf.productLoteShelves?.length > 0 && (
                                        <Box mb={2}>
                                          <Typography variant="caption" color="textSecondary">
                                            Inventário:
                                          </Typography>
                                          {shelf.productLoteShelves.map((item) => (
                                            <Box key={item.publicId} display="flex" justifyContent="space-between" alignItems="center">
                                              <Typography variant="body2">
                                                {item.productName}
                                                {item.loteNumber && ` (${item.loteNumber})`}
                                              </Typography>
                                              <Chip size="small" label={`Qtd.: ${item.quantity}`} />
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                      
                                      <Box display="flex" gap={1} flexWrap="wrap">
                                        <Button
                                          size="small"
                                          startIcon={<CheckinIcon />}
                                          onClick={() => openInventoryDialog(shelf.publicId, 'checkin')}
                                        >
                                          Dar entrada
                                        </Button>
                                        <Button
                                          size="small"
                                          startIcon={<CheckoutIcon />}
                                          onClick={() => openInventoryDialog(shelf.publicId, 'checkout')}
                                        >
                                          Dar saída
                                        </Button>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                                Não há prateleiras neste armazenamento. Clique em «Adicionar prateleira» para criar uma.
                              </Typography>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Card>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                    Não há áreas de armazenamento neste local. Clique em «Adicionar armazenamento» para criar uma.
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Card>
      ))}

      {locations.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WarehouseIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Nenhum local encontrado
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Comece por criar o seu primeiro local para organizar o inventário.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setLocationDialogOpen(true)}
          >
            Adicionar o primeiro local
          </Button>
        </Paper>
      )}

      {/* Location Dialog */}
      <Dialog open={locationDialogOpen} onClose={() => setLocationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLocation ? 'Editar local' : 'Adicionar novo local'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do local"
            fullWidth
            variant="outlined"
            value={locationForm.name}
            onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={locationForm.description}
            onChange={(e) => setLocationForm(prev => ({ ...prev, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddLocation} variant="contained">
            {editingLocation ? 'Atualizar' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Storage Dialog */}
      <Dialog open={storageDialogOpen} onClose={() => setStorageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar área de armazenamento</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do armazenamento"
            fullWidth
            variant="outlined"
            value={storageForm.name}
            onChange={(e) => setStorageForm(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={storageForm.description}
            onChange={(e) => setStorageForm(prev => ({ ...prev, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStorageDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddStorage} variant="contained">Adicionar armazenamento</Button>
        </DialogActions>
      </Dialog>

      {/* Shelf Dialog */}
      <Dialog open={shelfDialogOpen} onClose={() => setShelfDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar prateleira</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da prateleira"
            fullWidth
            variant="outlined"
            value={shelfForm.name}
            onChange={(e) => setShelfForm(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={shelfForm.description}
            onChange={(e) => setShelfForm(prev => ({ ...prev, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShelfDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddShelf} variant="contained">Adicionar prateleira</Button>
        </DialogActions>
      </Dialog>

      {/* Inventory Dialog */}
      <Dialog open={inventoryDialogOpen} onClose={() => setInventoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {inventoryAction === 'checkin' ? 'Dar entrada de produtos' : 'Dar saída de produtos'}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="Produto"
            fullWidth
            variant="outlined"
            value={inventoryForm.productPublicId}
            onChange={(e) => setInventoryForm(prev => ({ ...prev, productPublicId: e.target.value }))}
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
          >
            <option value="">Selecione um produto</option>
            {products.map((product) => (
              <option key={product.publicId} value={product.publicId}>
                {product.name}
              </option>
            ))}
          </TextField>
          
          <TextField
            margin="dense"
            label="Número do lote"
            fullWidth
            variant="outlined"
            value={inventoryForm.loteNumber}
            onChange={(e) => setInventoryForm(prev => ({ ...prev, loteNumber: e.target.value }))}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Quantidade"
            type="number"
            fullWidth
            variant="outlined"
            value={inventoryForm.quantity}
            onChange={(e) => setInventoryForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
            sx={{ mb: 2 }}
          />
          
          {inventoryAction === 'checkin' && (
            <TextField
              margin="dense"
              label="Data de validade"
              type="date"
              fullWidth
              variant="outlined"
              value={inventoryForm.expirationDate}
              onChange={(e) => setInventoryForm(prev => ({ ...prev, expirationDate: e.target.value }))}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          
          <TextField
            margin="dense"
            label="Notas"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={inventoryForm.notes}
            onChange={(e) => setInventoryForm(prev => ({ ...prev, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInventoryDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleInventoryOperation} variant="contained">
            {inventoryAction === 'checkin' ? 'Dar entrada' : 'Dar saída'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HierarchicalLocationList;
